<template>
    <Overlay :show="showLoading">
        <ConcernElementsModal
            ref="concernElementsModal"
            :product="product"
            :included-substances="includedSubstances"
            :excluded-substances="excludedSubstances"
            :company-has-information="true"
            :instruction="$t('Page(ReceivedRequest):Component(ConcernElementsModal):hint')"
            :legislations="substanceListLegislations"
            @listQuestionStateUpdated="listQuestionStateUpdated"
            @undefined="onUndefinedSubstance"
            @include="include"
            @exclude="excludeModal"
            @modalClosed="modalClosed"
        />

        <ForwardedRequests ref="activeSentRequestsModal"/>
        <b-row class="pb-1 mt-1" v-if="isInformationOwnerRadioVisible">
            <b-col>
                {{ $t('Page(ReceivedRequest):Component(Text):who-has-information-question') }}
            </b-col>
        </b-row>
        <b-row class="vertical-flow-concern-elements" v-if="isInformationOwnerRadioVisible">
            <b-col>
                <b-form-radio
                    value="supplier"
                    name="who-has-information"
                    v-model="informationOwner"
                    @change="informationOwnerChange"
                >
                    {{ $t('Page(ReceivedRequest):Component(Radio):text-supplier-has-information') }}
                </b-form-radio>
                <b-form-radio
                    value="we-partially"
                    name="who-has-information"
                    v-model="informationOwner"
                    @change="informationOwnerChange"
                >
                    {{ $t('Page(ReceivedRequest):Component(Radio):text-we-partially-have-information') }}
                </b-form-radio>
                <b-form-radio
                    value="we"
                    name="who-has-information"
                    v-model="informationOwner"
                    @change="informationOwnerChange"
                >
                    {{ $t('Page(ReceivedRequest):Component(Radio):text-we-have-information') }}
                </b-form-radio>
            </b-col>
        </b-row>
        <Transition name="fade">
            <b-row class="mt-1" v-show="isForwardVisible">
                <b-col>
                    <Button size="sm" @click="forward">
                        {{ $t('Component(Button):forward-to-supplier') }}
                    </Button>

                    <Button v-if="activeSentRequests.length > 0" size="sm" class="ml-1" @click="showSentRequests">
                        {{ $t('Component(Button):browse-sent-requests') }}
                    </Button>
                </b-col>
            </b-row>
        </Transition>

        <div v-if="showConcernElementsDeclaration">
            <div v-if="isComplex" class="pt-2">
                <p>{{ $t('Page(ReceivedRequest):Component(Text):components-tree-instruction') }}</p>
                <ComponentsTree
                    :hierarchy="hierarchy"
                    @checked="onComponentCheck"
                    @unchecked="onComponentUncheck"
                />

                <b-row v-if="showProductWeight">
                    <b-col>
                        <InstructionText variant="warning" v-if="showProductWeightMessage">
                            {{ $t('Page(ReceivedRequest):Component(Text):product-weight-message') }}
                        </InstructionText>

                        <b-form-group
                            :label="$t('Component(Placeholder):product-weight')"
                            label-for="productWeight"
                        >
                            <b-form-input
                                type="number"
                                class="input-bg-warning"
                                id="productWeight"
                                :placeholder="$t('Component(Placeholder):product-weight')"
                                v-model="weight"
                                @input="productWeightChanged"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <app-collapse
                    accordion
                    type="default"
                >
                    <ComponentConcernElements
                        v-for="(component, index) in visibleComponents"
                        :key="makeComponentKey(component)"
                        :component="component"
                        :substance-component="substanceComponent"
                        :is-material-category-mandatory="isMaterialCategoryMandatory"
                        :compact-view="compactView"
                        :can-add-placement-components="canAddPlacementComponents"
                        :mixture-categories="mixtureCategories"
                        :material-categories="materialCategories"
                        :additional-materials="additionalMaterials"
                        :legislations="request.legislations"
                        :substance-lists="substanceLists"
                        @stateUpdated="onComponentStateUpdate"
                        @substancesSaved="substancesSaved"
                        @hasConcernSubstancesChange="emitValidity"
                        @substanceIncluded="componentSubstanceIncluded"
                        @substanceExcluded="componentSubstanceExcluded"
                        @substanceExcludedAndSaved="$emit('componentSubstanceExcludedAndSaved')"
                    />
                </app-collapse>
            </div>
            <div v-else-if="showSimpleProductSubstances">
                <InstructionText
                    v-if="showSubstanceDetailsMessage"
                    :class="{'mt-1': isForwardVisible || isInformationOwnerRadioVisible, 'mb-0': true}"
                    :variant="substanceDetailsMessage.variant"
                >
                    {{ substanceDetailsMessage.message }}
                </InstructionText>

                <SelectConcernElementsButton
                    class="mt-1"
                    @selectConcernElements="selectConcernElements"
                />

                <b-row class="pt-1" v-if="showProductWeight">
                    <b-col>
                        <InstructionText variant="warning" v-if="showProductWeightMessage">
                            {{ $t('Page(ReceivedRequest):Component(Text):product-weight-message') }}
                        </InstructionText>

                        <b-form-group
                            :label="$t('Component(Placeholder):product-weight')"
                            label-for="productWeight"
                        >
                            <b-form-input
                                type="number"
                                class="input-bg-warning"
                                id="productWeight"
                                :placeholder="$t('Component(Placeholder):product-weight')"
                                v-model="weight"
                                @input="productWeightChanged"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <div class="pt-1">
                    <component
                        :is="substanceRenderer"
                        v-for="substance in includedSubstances"
                        :key="substance.substance.id"
                        :read-only="false"
                        :compact-view="compactView"
                        :substance="substance"
                        :substance-lists="substanceLists"
                        @exclude="exclude"
                        @change="substanceChange"
                    />
                </div>
            </div>
        </div>
    </Overlay>
</template>

<script>
import { BCol, BRow, BFormCheckbox, BButton, BFormRadio, BFormInput, BFormGroup } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ConcernElementsModal from "@/views/product/components/product-card/ConcernElementsModal";
import substanceDictionaries from "@core/mixins/substance-dictionaries";
import ConcernElement from "@/views/request/received/flow/components/concern-element/ConcernElement";
import ComponentConcernElements
    from "@/views/request/received/flow/components/concern-element/ComponentConcernElements";
import {ConcernElementPlacementOrder, ProductProvider, receivedRequestProvider} from "@core/domain/product";
import SelectConcernElementsButton
    from "@/views/request/received/flow/components/vertiacal-steps/SelectConcernElementsButton";
import ComponentsTree from "@/views/request/received/flow/components/concern-element/ComponentsTree";
import SelectOrMakeProductModal from "@core/components/product/SelectOrMakeProductModal";
import OrderSelector from "@/views/request/received/flow/components/vertiacal-steps/OrderSelector";
import {
    ConcernElementsInformationOwner,
    ProductType,
    getNameOrId,
    ProductIdentifiers,
    SubstanceState
} from "@/libs/product";
import {SubstanceApi} from "@core/api";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import {Button} from "@core/components/common";
import ForwardedRequests from "@/views/request/received/flow/components/vertiacal-steps/ForwardedRequestsModal";
import {
    DEFAULT_SUBSTANCE_COMPONENT,
    resolveSubstanceComponent,
} from '@/views/product/components/product-card/composition/substance-editor/registry'
import Overlay from "@core/components/common/Overlay.vue";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import ActiveSubstanceListsMixin from "@/views/product/components/product-card/ActiveSubstanceListsMixin";
import {getAllSubstanceLists} from "@core/domain/substance";

export default {
    mixins: [substanceDictionaries, ActiveSubstanceListsMixin],
    directives: {Ripple},
    components: {
        BFormCheckbox, BCol, BRow, BButton, BFormRadio, ConcernElementsModal, ConcernElement, ComponentConcernElements,
        SelectConcernElementsButton, ComponentsTree, SelectOrMakeProductModal, OrderSelector,
        AppCollapse, AppCollapseItem, Button, ForwardedRequests, Overlay, InstructionText,
        BFormInput, BFormGroup,
    },
    props: {
        compactView: {
            type: Boolean,
            default: false
        },
        request: {
            required: true
        },
        product: {
            required: true,
        },
        includedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        excludedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        componentsConcernElements: {
            type: Array,
            default() {
                return [];
            }
        },
        showProductWeight: {
            type: Boolean,
            default: false
        },
        showProductWeightMessage: {
            type: Boolean,
            default: false
        },
        showInformationOwnerSelection: {
            type: Boolean,
            default: true
        },
        substanceComponent: {
            type: String,
            default: DEFAULT_SUBSTANCE_COMPONENT,
        }
    },
    data() {
        return {
            showLoading: false,
            informationOwner: null,
            weHaveInformation: this.hasConcernSubstances,
            hasConcernElements: false,
            substanceList: null,
            isComplex: null,
            hierarchy: {
                children: []
            },
            checkedComponent: null,
            filterComponents: [],
            visibleComponents: [],
            visibleComponentIds: [],
            order: ConcernElementPlacementOrder.TOP_DOWN,

            hasConcernElementsTimeout: null,

            activeSentRequests: [],
            substanceLists: [],

            concernElementsModalChanged: false,
            listQuestionStateChanged: false,
            timerId: null,

            weight: null,
            weightTimeoutId: null,
        };
    },

    methods: {
        listQuestionStateUpdated(list, state) {
            this.listQuestionStateChanged = true;
            this.$emit('listQuestionStateUpdated', list, state);
        },
        onComponentStateUpdate() {
            this.emitValidity();
        },
        substancesSaved() {
            this.emitValidity();
            this.$emit('componentsModalClosed');
        },
        componentSubstanceIncluded(componentContainer, substance) {
            this.recalculateWeHaveInformationCheckboxState();

            if (true !== this.getProductHasConcernSubstances()) {
                this.productHasConcernElements(true);
            }

            this.emitValidity();
            this.$emit('componentSubstanceIncluded', componentContainer, substance);
        },
        componentSubstanceExcluded(componentContainer, substance) {
            this.recalculateWeHaveInformationCheckboxState();
            this.emitValidity();
            this.$emit('componentSubstanceExcluded', componentContainer, substance);
        },
        makeComponentKey(component) {
            let key = [component.component.id];
            component.parents.forEach(p => key.push(p.id));
            return key.join('_');
        },
        recalculateVisibleComponents() {
            this.visibleComponentIds = [];
            this.visibleComponents = this.componentsConcernElements.filter(this.isConcernElementsComponentVisible);
        },
        isConcernElementsComponentVisible(componentContainer) {
            if (this.request.product.id === componentContainer.component.id) {
                return false;
            }

            if (this.visibleComponentIds.includes(componentContainer.component.id)) {
                return false;
            }

            if (this.filterComponents.length === 0) {
                this.visibleComponentIds.push(componentContainer.component.id);
                return true;
            }

            if (this.filterComponents.find(c => c.id === componentContainer.component.id)) {
                this.visibleComponentIds.push(componentContainer.component.id);
                return true;
            }

            let isVisible = componentContainer.parents.find(
                p => this.filterComponents.find(c => c.id === p.id)
            ) instanceof Object;

            if (isVisible) {
                this.visibleComponentIds.push(componentContainer.component.id);
            }

            return isVisible;
        },
        excludeFromFilter(component) {
            let index = this.filterComponents.findIndex(c => c.id === component.id);
            if (index >= 0) {
                this.filterComponents.splice(index, 1);
                this.recalculateVisibleComponents();
            }
        },
        onComponentUncheck(component) {
            this.excludeFromFilter(component);
        },
        onComponentCheck(component) {
            if (!this.filterComponents.find(c => c.id === component.id)) {
                if (component !== this.hierarchy) {
                    this.hierarchy.selected = false;
                }
                this.filterComponents.forEach(cmp => cmp.selected = false);
                this.filterComponents.splice(0);

                this.filterComponents.push(component);
                this.recalculateVisibleComponents();
            }
        },
        informationOwnerChange() {
            this.$emit('informationOwnerChange', this.informationOwner)

            //  when supplier does not have information we need to answer to question if this product has concern
            //  substances only when we do not have any information
            if (ConcernElementsInformationOwner.WE === this.informationOwner) {
                const {productHasConcernSubstances, doWeHaveInformation} = this.gatherInformationWeHave();
                if (null !== this.getProductHasConcernSubstances() && !productHasConcernSubstances && !doWeHaveInformation) {
                    //  unset product has concern substances
                    this.productHasConcernElements(null);
                }
            }

            this.emitValidity();
        },
        forward() {
            this.$emit('forward');
        },
        selectConcernElements() {
            this.$refs.concernElementsModal.show();
        },
        stepActivation() {
            if (false === this.isInformationOwnerRadioVisible) {
                if (this.request.product.substanceState === SubstanceState.ALL_UNDEFINED) {
                    this.selectConcernElements();
                }
            }
        },
        productHasConcernElements(hasConcernElements) {
            this.$emit('hasConcernSubstances', hasConcernElements);

            //  in case product does not have concern elements
            //  and we have all information
            //  we can set that component does not have concern elements by default for all components where
            //  state is not defined
            clearTimeout(this.hasConcernElementsTimeout);
            this.hasConcernElementsTimeout = setTimeout(() => {
                if (false === hasConcernElements && this.informationOwner === ConcernElementsInformationOwner.WE) {
                    this.componentsConcernElements.forEach(container => {
                        if (null === container.component.hasConcernSubstances) {
                            container.component.hasConcernSubstances = false;

                            const product = ProductProvider(container.component.id);
                            product.getCard()
                                .then(() => {
                                    product.setHasConcernSubstances(false);
                                    product.saveCard();
                                });
                        }
                    });
                }
            }, 1000);
        },
        onIsComplexChange() {
            this.$emit('isComplex', this.isComplex);
        },
        modalClosed() {
            if (this.concernElementsModalChanged || this.listQuestionStateChanged) {
                this.$emit('modalClosed');
            }
            this.concernElementsModalChanged = false;
            this.listQuestionStateChanged = false;
        },
        onUndefinedSubstance(substance) {
            this.concernElementsModalChanged = true;
            this.$emit('undefined', substance, false);
        },
        include(substance, substanceListIds = []) {
            this.concernElementsModalChanged = true;
            this.$emit('include', substance, substanceListIds);
        },
        excludeModal(substance) {
            this.concernElementsModalChanged = true;
            this.$emit('excludeModal', substance);
        },
        exclude(includedSubstance) {
            this.showLoading = true;
            this.$emit('undefined', includedSubstance.substance, true, () => {
                receivedRequestProvider(this.request.id)
                    .refreshSubstanceState()
                    .finally(() => this.showLoading = false)
            });
        },
        substanceChange(substance, properties) {
            clearTimeout(this.timerId);

            this.timerId = setTimeout(() => {
                this.showLoading = true;
                this.$emit(
                    'substanceChange',
                    substance,
                    properties,
                    () => {
                        this.showLoading = false
                    }
                );
            }, 2000);
        },
        initHierarchy(h) {
            this.$set(h, 'selected', false);
            h.children.forEach(c => {
                this.initHierarchy(c);
            });
        },
        fetchHierarchy() {
            return this.getRequestProduct()
                .getHierarchy()
                .then(h => {
                    const hierarcy = JSON.parse(JSON.stringify(h));
                    this.initHierarchy(hierarcy)
                    this.hierarchy = hierarcy;
                    if (this.hierarchy.children.length === 0) {
                        this.hierarchy.selected = true;
                        this.checkedComponent = this.hierarchy;
                    } else {
                        this.checkSelectedComponent();
                    }
                });
        },
        checkSelectedComponent(component) {
            if (!this.checkedComponent) {
                return;
            }
            component = component || this.hierarchy;
            if (component.id === this.checkedComponent.id) {
                this.checkedComponent = component;
                this.checkedComponent.selected = true;
            } else {
                component.children.forEach(this.checkSelectedComponent);
            }
        },

        getRequestProduct() {
            return ProductProvider(this.request.product.id);
        },

        gatherInformationWeHave() {
            let productHasConcernSubstances = false;
            let doWeHaveInformation = null;
            if (this.isComplex) {
                this.componentsConcernElements.forEach(container => {
                    if (container.substances.length > 0) {
                    // if (container.concernElements.length > 0) {
                        productHasConcernSubstances = true;
                        doWeHaveInformation = true
                    }
                    if (container.component.hasConcernSubstances === false) {
                        doWeHaveInformation = true
                    }
                });
            } else {
                if (this.includedSubstances.length > 0) {
                    productHasConcernSubstances = true;
                    doWeHaveInformation = true;
                }
            }
            return {productHasConcernSubstances, doWeHaveInformation};
        },

        recalculateWeHaveInformationCheckboxState() {
            if (this.componentsConcernElements.length > 0) {
                const {productHasConcernSubstances, doWeHaveInformation} = this.gatherInformationWeHave();
                if (false === this.weHaveInformation && (productHasConcernSubstances || doWeHaveInformation)) {
                    this.weHaveInformation = true;
                    //  update product has concern substances in case it is not
                    //  it should be done on the backend side (maybe it is)
                    if (this.request.product.hasConcernSubstances !== true && productHasConcernSubstances) {
                        this.productHasConcernElements(true);
                    }
                }
            }
        },

        getProductHasConcernSubstances() {
            return this.request.product.hasConcernSubstances;
        },

        emitValidity() {
            let isValid = false;
            const validStates = [
                SubstanceState.ALL_DEFINED_INCLUDES_SUBSTANCES,
                SubstanceState.ALL_DEFINED_DOES_NOT_INCLUDE_SUBSTANCES
            ];

            if (false === this.isComplex) {
                if (
                    ConcernElementsInformationOwner.WE === this.informationOwner
                    || ConcernElementsInformationOwner.WE_PARTIALLY === this.informationOwner
                ) {
                    if (validStates.includes(this.substanceState)) {
                        isValid = true;
                    }
                }
            } else if (true === this.isComplex) {
                const countOfComponentsInInvestigationState = this.componentsConcernElements.filter(container => {
                    return !validStates.includes(container.state);
                }).length;

                if (0 === countOfComponentsInInvestigationState) {
                    isValid = true;
                }
            }

            this.$emit('validityChanged', isValid)
        },

        showSentRequests() {
            this.$refs.activeSentRequestsModal.show(new Promise((resolve) => resolve(this.activeSentRequests)));
        },

        fetchSentRequests() {
            receivedRequestProvider(this.request.id)
                .getForwardedRequests()
                .then(r => {
                    this.activeSentRequests = r.data;
                })
        },

        retrieveSubstanceLists() {
            getAllSubstanceLists().then(lists => {
                this.substanceLists = this.activeSubstanceLists(
                    lists,
                    this.request.legislations.map(l => l.id)
                );
            });
        },

        productWeightChanged() {
            clearTimeout(this.weightTimeoutId);
            this.weightTimeoutId = setTimeout(() => {
                this.$emit('characteristicsChange', {
                    weight: parseFloat(this.weight)
                })
            }, 1000);

        }
    },
    computed: {
        substanceRenderer() {
            return resolveSubstanceComponent(this.substanceComponent)
        },
        substanceListLegislations() {
            return this.request.legislations.map(l => l.id);
        },
        isMaterialCategoryMandatory() {
            //  HAM-334: mandatory when suppliers scip number was not presented
            //  user can declare suppliers scip number only for purchased product type
            if (ProductType.PURCHASED === this.request.product.type) {
                return getNameOrId(this.request.product.identifiers, ProductIdentifiers.SUPPLIERS_SCIP_NUMBER) === '';
            }

            return true;
        },
        showConcernElementsDeclaration() {
            return ConcernElementsInformationOwner.WE === this.informationOwner
                || ConcernElementsInformationOwner.WE_PARTIALLY === this.informationOwner;
        },
        showHasConcernElementsChoice() {
            return ConcernElementsInformationOwner.WE === this.informationOwner
                || ConcernElementsInformationOwner.WE_PARTIALLY === this.informationOwner;
        },
        isNoChoiceOfHasConcernElementsDisabled() {
            if (this.includedSubstances.length > 0) {
                return true;
            }
            const {productHasConcernSubstances, doWeHaveInformation} = this.gatherInformationWeHave();
            return productHasConcernSubstances;
        },
        isTopDownOrder() {
            return this.order === ConcernElementPlacementOrder.TOP_DOWN;
        },
        isAppendComponentButtonDisabled() {
            if (!this.checkedComponent) {
                return true;
            }
            if (this.order === ConcernElementPlacementOrder.TOP_DOWN) {
                return !this.checkedComponent.mayIncludeComponents;
            }

            return false;
        },
        isInformationOwnerRadioVisible() {
            return this.showInformationOwnerSelection
                && !(false === this.isComplex && ProductType.OWN === this.request.product.type);
        },
        isForwardVisible() {
            return ConcernElementsInformationOwner.WE_PARTIALLY === this.informationOwner
                || ConcernElementsInformationOwner.SUPPLIER === this.informationOwner;
        },
        canAddPlacementComponents() {
            return !this.$store.getters.subscription.isActive || !this.request.product.isComplex;
        },
        hasConcernSubstances() {
            return this.request.product.hasConcernSubstances || this.includedSubstances.length > 0;
        },
        hasConcernSubstancesProductValue() {
            return this.request.product.hasConcernSubstances;
        },
        isComplexProduct() {
            return this.request.isComplexProduct;
        },

        showSimpleProductSubstances() {
            if (this.isComplex) {
                return false;
            }

            if (this.informationOwner === ConcernElementsInformationOwner.WE_PARTIALLY) {
                return true
            }

            return this.informationOwner === ConcernElementsInformationOwner.WE;
        },
        informationOwnerToWatch() {
            return this.request.informationOwner;
        },
        substanceState() {
            return this.request.substanceState;
        },
        showSubstanceDetailsMessage() {
            return true;
        },
        substanceDetailsMessage() {
            switch (this.substanceState) {
                case SubstanceState.ALL_DEFINED_DOES_NOT_INCLUDE_SUBSTANCES:
                    return {
                        message: this.$t('Page(ReceivedRequest):Component(ConcernElements):message-all-declared-not-includes'),
                        variant: 'success'
                    };

                case SubstanceState.ALL_DEFINED_INCLUDES_SUBSTANCES:
                    return {
                        message: this.$t('Page(ReceivedRequest):Component(ConcernElements):message-all-declared-includes'),
                        variant: 'success'
                    };

                case SubstanceState.SOME_DEFINED_DOES_NOT_INCLUDE_SUBSTANCES:
                case SubstanceState.SOME_DEFINED_INCLUDES_SUBSTANCES:
                case SubstanceState.ALL_UNDEFINED:
                default:
                    return {
                        message: this.$t('Page(ReceivedRequest):Component(ConcernElements):message-not-all-declared'),
                        variant: 'danger'
                    };
            }
        },
    },
    watch: {
        substanceState() {
            this.emitValidity();
        },
        hasConcernSubstancesProductValue() {
            this.emitValidity();
        },
        isComplexProduct(isComplex) {
            this.isComplex = isComplex;
        },
        informationOwnerToWatch(value) {
            this.informationOwner = value;
        },
        request(r) {
            this.informationOwner = this.request.informationOwner;
            this.weHaveInformation = this.hasConcernSubstances;
            this.isComplex = this.request.isComplexProduct;

            if (!this.hierarchy.id && this.request.product.id) {
                this
                    .fetchHierarchy()
                    .then(() => {
                        this.checkedComponent = this.hierarchy;
                        this.$set(this.hierarchy, 'isOpen', true);
                        this.checkSelectedComponent();
                    });
            }

            if (
                this.informationOwner === ConcernElementsInformationOwner.WE_PARTIALLY
                || this.informationOwner === ConcernElementsInformationOwner.SUPPLIER
            ) {
                this.fetchSentRequests();
            }
            this.emitValidity();
            this.retrieveSubstanceLists();
        },
        product() {
            this.weight = this.product.characteristics.weight;
        },
        includedSubstances() {
            this.emitValidity();
        },
        componentsConcernElements() {
            this.recalculateWeHaveInformationCheckboxState();
            this.recalculateVisibleComponents();
            this.emitValidity();
        },
    },
    created() {
        setTimeout(() => {
            this.initSubstanceDictionaries();
        }, 500);

        this.retrieveSubstanceLists();
    },
}
</script>

<style lang="scss">
.vertical-flow-concern-elements .custom-control-label {
    font-size: 1rem;
    cursor: pointer;
}
</style>
